import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, ShieldCheck, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StickyActionBar } from "@/components/StickyActionBar";
import { BetaBanner } from "@/components/BetaBanner";
import { BetaFeedbackButton } from "@/components/BetaFeedback";
import { LaunchSignup } from "@/components/LaunchSignup";
import { useBetaStatus, betaMetric } from "@/hooks/useBetaStatus";
import { usePlatformCopy } from "@/hooks/usePlatformCopy";
import { COPY } from "@/lib/copy";

const NAV = [
  { to: "/", label: "Home", end: true },
  {
    label: "Witness Your Vote",
    items: [
      { to: "/how-it-works", label: "How It Works" },
      { to: "/declare", label: "Witness My Vote" },
      { to: "/verify", label: "Verify Receipt" },
      { to: "/privacy", label: "Privacy" },
      { to: "/legal", label: "Legal" },
    ],
  },
  {
    label: "Elections",
    items: [
      { to: "/elections", label: "Election Centre" },
      { to: "/elections/find-my-ward", label: "Find My Ward" },
      { to: "/elections/map", label: "Ontario Election Map" },
      { to: "/ontario", label: "Ontario Municipalities" },
      { to: "/coverage", label: "Where We're Available" },
      { to: "/kawartha-lakes-2026", label: "Kawartha Lakes 2026" },
    ],
  },
  {
    label: "Community Voice",
    items: [
      { to: "/polls", label: "Community Polls" },
      { to: "/community-voice", label: "Community Voice" },
      { to: "/issue-map", label: "Ontario Issue Map" },
    ],
  },
  {
    label: "Candidates",
    items: [
      { to: "/find-a-candidate", label: "Find a Candidate" },
      { to: "/candidates", label: "Participating Candidates" },
      { to: "/ask-the-candidates", label: "Ask the Candidates" },
      { to: "/candidates/compare", label: "Compare the Candidates" },
      { to: "/candidate-filings", label: "Latest Candidate Filings" },
      { to: "/candidate-application", label: "Candidate Sign-Up" },
      { to: "/invite-a-candidate", label: "Invite a Candidate" },
    ],
  },
  {
    label: "News",
    items: [
      { to: "/news", label: "Newsroom" },
      { to: "/news?category=election_news", label: "Ontario Election News" },
      { to: "/candidate-filings", label: "New Candidate Updates" },
    ],
  },
  {
    label: "About",
    items: [
      { to: "/about", label: "About Witness Your Vote" },
      { to: "/accountability", label: "Accountability" },
      { to: "/alerts", label: "Sign Up for Alerts" },
      { to: "/contact", label: "Contact" },
      { to: "/pita", label: "PITA Information" },
      { to: "/pita/request", label: "Request PITA" },
    ],
  },
];

const FOOTER_LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/declare", label: "Witness My Vote" },
  { to: "/verify", label: "Verify a Receipt" },
  { to: "/elections", label: "Election Centre" },
  { to: "/elections/find-my-ward", label: "Find My Ward" },
  { to: "/elections/map", label: "Ontario Election Map" },
  { to: "/coverage", label: "Where We're Available" },
  { to: "/candidates", label: "Participating Candidates" },
  { to: "/candidate-filings", label: "Latest Candidate Filings" },
  { to: "/candidate-application", label: "Candidate Sign-Up" },
  { to: "/news", label: "News" },
  { to: "/alerts", label: "Sign Up for Alerts" },
  { to: "/accountability", label: "Accountability" },
  { to: "/pita", label: "PITA" },
  { to: "/about", label: "About" },
  { to: "/privacy", label: "Privacy" },
  { to: "/legal", label: "Legal" },
  { to: "/contact", label: "Contact" },
  { to: "/kawartha-lakes-2026", label: "Kawartha Lakes 2026" },
];

export default function PublicLayout() {
  const copy = usePlatformCopy();
  const beta = useBetaStatus();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpen(false);
  }, [location.pathname]);

  // privacy-safe aggregate visit metric — once per session, never linked to declarations
  useEffect(() => {
    try {
      if (!sessionStorage.getItem("wyv_beta_visit")) {
        sessionStorage.setItem("wyv_beta_visit", "1");
        betaMetric("beta_page_visit");
      }
    } catch { /* ignore */ }
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {copy.test_mode && (
        <div
          data-testid="test-mode-banner"
          className="bg-amber-100 border-b border-amber-300 text-amber-900 text-center text-xs sm:text-sm font-semibold px-4 py-1.5"
        >
          TEST MODE — This platform is in preview. All declarations are labelled TEST and are not live election records.
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="wyv-container flex items-center justify-between h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="header-logo-link">
            <img
              src="/branding/line-light.webp"
              alt="Witness Your Vote — witnessyourvote.com"
              className="h-9 sm:h-10 w-auto"
              data-testid="header-logo-img"
            />
            {beta?.beta_mode && (
              <span className="rounded-full bg-emerald-600 text-white text-[9px] sm:text-[10px] font-bold tracking-widest px-2 py-0.5 shrink-0"
                    data-testid="header-beta-badge" aria-label="WitnessYourVote is in beta">
                BETA
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" aria-label="Main navigation">
            {NAV.map((item) =>
              item.items ? (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger
                    className="nav-link inline-flex items-center gap-1 whitespace-nowrap"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label} <ChevronDown className="h-3.5 w-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-white">
                    {item.items.map((sub) => (
                      <DropdownMenuItem key={sub.to} asChild>
                        <Link to={sub.to} className="cursor-pointer" data-testid={`nav-sub-${sub.to.replace(/[/?=]/g, "-").replace(/^-/, "")}`}>
                          {sub.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? "active" : ""}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center shrink-0">
            <Button asChild className="bg-accent hover:bg-accent/90" data-testid="header-witness-button">
              <Link to="/declare"><ShieldCheck className="h-4 w-4 mr-1.5" /> Witness Your Vote</Link>
            </Button>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="outline" size="icon" aria-label="Open menu" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-80 overflow-y-auto">
              <SheetTitle className="font-heading text-left">Menu</SheetTitle>
              <nav className="mt-5 flex flex-col gap-0.5 pb-8" aria-label="Mobile navigation">
                <Button asChild className="bg-accent hover:bg-accent/90 mb-3" data-testid="mobile-witness-button">
                  <Link to="/declare"><ShieldCheck className="h-4 w-4 mr-1.5" /> Witness Your Vote</Link>
                </Button>
                {NAV.map((item) =>
                  item.items ? (
                    <div key={item.label} className="mt-2">
                      <p className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{item.label}</p>
                      {item.items.map((sub) => (
                        <NavLink key={sub.to} to={sub.to}
                          className={({ isActive }) =>
                            `block px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-secondary text-primary font-semibold" : "text-slate-700 hover:bg-secondary"}`}>
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : (
                    <NavLink key={item.to} to={item.to} end={item.end}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-secondary text-primary font-semibold" : "text-slate-700 hover:bg-secondary"}`}>
                      {item.label}
                    </NavLink>
                  )
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <BetaBanner />

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-slate-900 text-slate-300 mt-16 pb-20" data-testid="site-footer">
        <div className="wyv-container py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="md:col-span-2">
              {/* Branding: LINE-DARK on mobile; large SQUARE-DARK on desktop (dark footer) */}
              <img
                src="/branding/line-dark.webp"
                alt="Witness Your Vote — witnessyourvote.com"
                className="h-10 w-auto mb-4 md:hidden"
                data-testid="footer-logo-line"
              />
              <img
                src="/branding/square-dark.png"
                alt="Witness Your Vote — witnessyourvote.com"
                className="hidden md:block w-52 lg:w-60 h-auto mb-5"
                data-testid="footer-logo-square"
              />
              <p className="text-sm text-slate-400 mb-5">{COPY.slogan}</p>
              <div className="space-y-3 text-xs leading-relaxed text-slate-400 max-w-2xl">
                <p>{copy.disclaimers?.independent || COPY.disclaimers.independent}</p>
                <p>{copy.disclaimers?.not_replacement || COPY.disclaimers.notReplacement}</p>
                <p>{copy.disclaimers?.voluntary || COPY.disclaimers.voluntary}</p>
                <p>{copy.disclaimers?.governance || COPY.disclaimers.governance}</p>
              </div>
            </div>
            <div>
              <h3 className="text-white font-heading font-semibold text-sm mb-4">Navigate</h3>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {FOOTER_LINKS.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className="hover:text-white hover:underline">{item.label}</Link>
                  </li>
                ))}
                <li>
                  <Link to="/beta" className="hover:text-white hover:underline" data-testid="footer-beta-link">What is BETA?</Link>
                </li>
                <li>
                  <Link to="/admin" className="hover:text-white hover:underline" data-testid="footer-admin-login">Admin Login</Link>
                </li>
              </ul>
              {beta?.beta_mode && (
                <div className="mt-8 rounded-xl border border-slate-800 bg-slate-800/40 p-4" data-testid="footer-signup-block">
                  <h3 className="text-white font-heading font-semibold text-sm">WitnessYourVote Updates</h3>
                  <p className="text-xs text-slate-400 mt-0.5 mb-3">Get notified when we officially launch.</p>
                  <LaunchSignup variant="footer" sourcePage="footer" />
                  <div className="mt-4 pt-3 border-t border-slate-800">
                    <BetaFeedbackButton dark />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-slate-800 mt-10 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-500">
            <p>© {new Date().getFullYear()} Witness Your Vote Ontario. An independent civic initiative.</p>
            <p>
              support@witnessyourvote.ca ·{" "}
              <Link to="/admin" className="hover:text-slate-300" data-testid="footer-admin-link">Administrator access</Link>
            </p>
          </div>
        </div>
      </footer>

      <StickyActionBar />
    </div>
  );
}
